import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Grid,
  Typography,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import * as Icons from '@mui/icons-material';
import { JMC_COLORS } from '../../styles/colors';
import * as Yup from 'yup';
import ProductionModelDialog from './Productionmodel/productionmodel';
import { get, post } from '../../pages/shared/api-service';
import { useNotification } from '../../pages/shared/hooks/notification';

function getChipColor(status) {
  const s = (status || '').toString().toLowerCase();
  switch (s) {
    case 'delivered':
    case 'dispatched':
    case 'completed':
      return 'success';
    case 'in transit':
    case 'pending':
      return 'warning';
    case 'scheduled':
      return 'info';
    case 'cancelled':
    case 'canceled':
      return 'error';
    default:
      return 'default';
  }
}

const STATUS_OPTIONS = ['scheduled', 'in transit', 'delivered', 'cancelled'];
function ProductionDispatch() {
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [orderBy, setOrderBy] = useState('dispatch_date');
  const [order, setOrder] = useState('desc');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [loading, setLoading] = useState(false);
  const { showNotification } = useNotification();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editRow, setEditRow] = useState(null);

  const [orders, setOrders] = useState([]);
  const orderOptions = useMemo(
    () => (orders || []).map(o => ({ value: o.order_id, label: `${o.order_id} - ${o.paper_grade || '-'}` })),
    [orders]
  );
  console.log("orderOptions----", orderOptions)
  const filtered = useMemo(() => {
    const q = search?.trim().toLowerCase();
    let list = rows.filter(o =>
      String(o.order_id || '').toLowerCase().includes(q) ||
      String(o.client_name || '').toLowerCase().includes(q)
    );
    if (statusFilter !== 'All') list = list.filter(o => (o.status || '').toString().toLowerCase() === statusFilter.toLowerCase());
    return list;
  }, [rows, search, statusFilter]);

  const sorted = useMemo(() => {
    const list = [...filtered];
    list.sort((a, b) => {
      let va = a[orderBy];
      let vb = b[orderBy];
      if (orderBy === 'dispatch_date' || orderBy === 'orderDate') {
        va = new Date(va).getTime();
        vb = new Date(vb).getTime();
      }
      if (va < vb) return order === 'asc' ? -1 : 1;
      if (va > vb) return order === 'asc' ? 1 : -1;
      return 0;
    });
    return list;
  }, [filtered, orderBy, order]);

  const paged = useMemo(() => {
    const start = page * rowsPerPage;
    return sorted.slice(start, start + rowsPerPage);
  }, [sorted, page, rowsPerPage]);

  const handleSort = (key) => {
    if (orderBy === key) {
      setOrder(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setOrderBy(key);
      setOrder('asc');
    }
  };

  const handleDelete = (row) => {
    setRows(prev => prev.filter(r => (r.dispatch_id || r.id) !== (row.dispatch_id || row.id)));
  };

  const initialValues = useMemo(() => ({
    order_id: editRow?.order_id || '',
    dispatch_quantity_tons: editRow?.dispatch_quantity_tons ?? '',
    dispatch_date: editRow?.dispatch_date || '',
    dispatch_time: editRow?.dispatch_time || (typeof editRow?.dispatch_date === 'string' && editRow.dispatch_date.includes('T') ? editRow.dispatch_date.slice(11, 16) : ''),
    trailer_information: editRow?.trailer_information || '',
    transport_type: editRow?.transport_type || '',
    route_details: editRow?.route_details || '',
    transport_company: editRow?.transport_company || '',
    client_name: editRow?.client_name || '',
    arrival_time: editRow?.arrival_time || '',
    dispatch_notes: editRow?.dispatch_notes || '',
    status: editRow?.status || 'Scheduled',
  }), [editRow]);

  const schema = useMemo(() => Yup.object().shape({
    // dispatch_id: auto-generated
    order_id: Yup.string().required('Order is required'),
    dispatch_quantity_tons: Yup.number().typeError('Enter a valid number').moreThan(0, 'Must be > 0').required('Required'),
    dispatch_date: Yup.date().required('Required'),
    dispatch_time: Yup.string().nullable(),
    trailer_information: Yup.string().required('Required'),
    transport_type: Yup.string().oneOf(['Full Load', 'Partial Load']).required('Required'),
    route_details: Yup.string().required('Required'),
    transport_company: Yup.string().nullable(),
    client_name: Yup.string().required('Required'),
    arrival_time: Yup.string().nullable(),
    dispatch_notes: Yup.string().nullable(),
    status: Yup.string().oneOf(STATUS_OPTIONS).required('Required'),
  }), []);

  const ReusableTextField = ({ id, name, label, value, onChange, onBlur, touched, error, type = 'text' }) => (
    <TextField
      fullWidth
      size="small"
      id={id}
      name={name}
      label={label}
      value={value ?? ''}
      onChange={onChange}
      onBlur={onBlur}
      error={Boolean(touched) && Boolean(error)}
      helperText={Boolean(touched) && error}
      type={type}
      InputLabelProps={type === 'date' ? { shrink: true } : undefined}
      sx={{
        m: 0,
        '& .MuiOutlinedInput-root': { borderRadius: '12px' },
        '& .MuiInputBase-input': { fontSize: 14 },
        '& .MuiInputLabel-root': { fontSize: 14 }
      }}
    />
  );

  const fieldBoxSx = { width: { xs: '100%', sm: 'calc(50% - 12px)' } };

  const getProductionOrders = async () => {
    try {
      setLoading(true);
      const response = await get('/api/orders');
      // console.log("getProductionOrders", response.orders);
      setOrders(response.orders);
      setDialogOpen(false)
    } catch (err) {
      console.error("getProductionOrders", err);
    } finally {
      // setLoading(false);
    }
  }

  const postProductionDispatches = async (values) => {
    try {
      // setLoading(true);
      console.log("[DispatchOrders] Create payload:", values);
      const payload = {
        ...values,
        status: (values.status || '').toString().toLowerCase(),
        transport_type: (values.transport_type || '').toString(),
      };
      const response = await post('/api/dispatch-entry', payload);
      setDialogOpen(false)
      console.log("postProductionDispatches", response);
      getProductionDispatches();
    } catch (err) {
      console.error("getProductionDispatches", err);
      showNotification(err.message, 'error', 3000);

    } finally {
      setLoading(false);
    }
  }

  const getProductionDispatches = async () => {
    try {
      setLoading(true);
      const response = await get('/api/dispatch-entries');
      setRows(response.dispatch_entries);
      // console.log("getProductionDispatches", response.dispatch_entries);
    } catch (err) {
      console.error("getProductionDispatches", err);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    getProductionOrders();
    getProductionDispatches();
  }, [])

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box >
      <Grid container alignItems="center" justifyContent="space-between" >
        <Grid item>
          <Typography variant="h6" fontWeight={600} sx={{ mb: 0 }}>Dispatch Orders</Typography>
        </Grid>
        <Grid item>
          <Button
            variant="contained"
            startIcon={<Icons.Add />}
            onClick={() => { setEditRow(null); setDialogOpen(true); }}
            sx={{
              backgroundColor: JMC_COLORS.gas,
              textTransform: 'none',
              '&:hover': { backgroundColor: JMC_COLORS.gas, filter: 'brightness(0.9)' }
            }}
          >
            Add Dispatch
          </Button>
        </Grid>
      </Grid>

      <Paper elevation={2} sx={{ p: 1.5, my: 2, borderRadius: 2 }}>
        <Grid container spacing={1.5} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              size="small"
              label="Search"
              value={search}
              onChange={(e) => { setPage(0); setSearch(e.target.value); }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px' } }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl
              fullWidth
              size="small"
              sx={{
                minWidth: '160px',
                '& .MuiOutlinedInput-root': { borderRadius: '12px' },
                '& .MuiInputBase-input': { fontSize: 14 },
                '& .MuiInputLabel-root': { fontSize: 14 }
              }}
            >
              <InputLabel id="status-filter-label">Status</InputLabel>
              <Select
                labelId="status-filter-label"
                label="Status"
                value={statusFilter}
                onChange={(e) => { setPage(0); setStatusFilter(e.target.value); }}
              >
                <MenuItem sx={{ fontSize: '14px' }} value="All">All</MenuItem>
                {STATUS_OPTIONS.map(s => (
                  <MenuItem key={s} value={s} sx={{ fontSize: '14px' }}>{s}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      <TableContainer component={Paper} sx={{ backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'transparent' : '#fafcff', borderRadius: 2 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sortDirection={orderBy === 'dispatch_id' ? order : false}>
                <TableSortLabel active={orderBy === 'dispatch_id'} direction={order} onClick={() => handleSort('dispatch_id')}>
                  Dispatch ID
                </TableSortLabel>
              </TableCell>
              <TableCell>Order ID</TableCell>
              <TableCell>Client Name</TableCell>
              <TableCell sortDirection={orderBy === 'dispatch_quantity_tons' ? order : false}>
                <TableSortLabel active={orderBy === 'dispatch_quantity_tons'} direction={order} onClick={() => handleSort('dispatch_quantity_tons')}>
                  Dispatch Qty (Tons)
                </TableSortLabel>
              </TableCell>
              <TableCell sortDirection={orderBy === 'dispatch_date' ? order : false}>
                <TableSortLabel active={orderBy === 'dispatch_date'} direction={order} onClick={() => handleSort('dispatch_date')}>
                  Dispatch Date
                </TableSortLabel>
              </TableCell>
              {/* <TableCell>Dispatch Time</TableCell> */}
              <TableCell>Arrival Time</TableCell>
              <TableCell>Transport Type</TableCell>
              <TableCell>Transport Company</TableCell>
              <TableCell>Route Details</TableCell>
              <TableCell>Dispatch Notes</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paged.map((row) => (
              <TableRow key={row.dispatch_id || row.id} hover>
                <TableCell>{row.dispatch_id || row.id}</TableCell>
                <TableCell>{row.order_id}</TableCell>
                <TableCell>{row.client_name || '-'}</TableCell>
                <TableCell>{row.dispatch_quantity_tons ?? row.quantity ?? '-'}</TableCell>
                <TableCell>{row.dispatch_date || row.orderDate || '-'}</TableCell>
                {/* <TableCell>{row.dispatch_time || (typeof row.dispatch_date === 'string' && row.dispatch_date.includes('T') ? row.dispatch_date.slice(11,16) : '-')}</TableCell> */}
                <TableCell>{row.arrival_time || '-'}</TableCell>
                <TableCell>{row.transport_type || '-'}</TableCell>
                <TableCell>{row.transport_company || '-'}</TableCell>
                <TableCell>{row.route_details || '-'}</TableCell>
                <TableCell>{row.dispatch_notes || '-'}</TableCell>
                <TableCell>
                  <Chip label={row.status} color={getChipColor(row.status)} size="small" />
                </TableCell>
                <TableCell align="center">
                  <Box sx={{ display: "flex", justifyContent: "center", gap: 1 }}>
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => { setEditRow(row); setDialogOpen(true); }}>
                        <Icons.Edit fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton size="small" color="error" onClick={() => handleDelete(row)}>
                        <Icons.Delete fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={sorted.length}
        page={page}
        onPageChange={(e, p) => setPage(p)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
        rowsPerPageOptions={[5, 10, 25]}
      />
      {
        dialogOpen && (
          <ProductionModelDialog
            open={dialogOpen}
            onClose={() => setDialogOpen(false)}
            mode="dispatch"
            titleNew="Add Dispatch"
            titleEdit="Edit Dispatch"
            initialValues={initialValues}
            validationSchema={schema}
            onSubmit={async (values, { setSubmitting, resetForm }) => {
              console.log('[DispatchOrders] Submit:', values);
              await postProductionDispatches(values);
              await getProductionDispatches();
              setSubmitting?.(false);
              resetForm?.();
              setDialogOpen(false);
            }}
            orderOptions={orderOptions}
            statusOptions={STATUS_OPTIONS}
          />
        )
      }
    </Box>
  );
}

export default ProductionDispatch;